import { Fragment } from 'react';
import type { FC, JSX, ReactElement } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { linkWithUrl, stripOuterParagraph } from '@eneco/cms-types';
import { addHours, formatAmsterdamTime, isTodayAmsterdam } from '@eneco/common/date';
import { getStaticPrefix } from '@eneco/common/env';
import RichText from '@eneco/components/RichText/RichText';
import { StretchBucket, StretchBucketSkeleton } from '@eneco/components/StretchBucket';
import useDC from '@eneco/dc/useDC';
import { type TransformedSlice, useDynamicPricing } from '@eneco/hooks/dynamicPricing';
import { useFormatter } from '@eneco/i18n';
import { Box, Grid, IconButton, Image, Popover, Stack, Stretch, Text } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { ElectricityIcon, ErrorIcon, GasIcon, InfoIcon } from '@eneco/sparky/icons';
import type { DC_Domain_Models_Products_ProductType } from '@monorepo-types/dc';

import type { DynamicPriceCardContent } from './dynamic-price-card-content.types';

type Product = Extract<DC_Domain_Models_Products_ProductType, 'electricity' | 'gas'>;

const imageHeight = '114px';

const imagePaths: Record<string, Record<Product, string>> = {
  eneco: {
    electricity: '/dynamicpricing/current-tariff-electricity.svg',
    gas: '/dynamicpricing/current-tariff-gas.svg',
  },
  oxxio: {
    electricity: '/dynamicpricing/current-tariff-electricity.png',
    gas: '/dynamicpricing/current-tariff-gas.png',
  },
};

/**
 * `DynamicPriceCardElectricityV2`/`DynamicPriceCardGasV2` blocks for the CMS-agnostic lane, serving
 * **both** providers.
 *
 * Mirrors `libs/sitecore/components/content/src/DynamicPriceCard/CurrentDynamicPriceCard.tsx` 1:1: the
 * current hourly price from `useDynamicPricing`, the next hour's price as footnote, and an error state
 * when no price is available. Drops the legacy `[link.value.text]` placeholder check in favour of
 * `linkWithUrl()`; `StretchBucket` already shows its button only with both a URL and a label.
 */
const DynamicPriceCard: FC<{ product: Product }> = ({ product }) => {
  const { label } = useDC();
  const { format, currency } = useFormatter();
  const { adapter } = useCms();
  const content = useContent<DynamicPriceCardContent>();
  const { data, isLoading: isDynamicPricingLoading, hasError } = useDynamicPricing(new Date());

  if (isDynamicPricingLoading) {
    return <StretchBucketSkeleton label={content.loadingLabel ?? ''} />;
  }

  const title = content.title ?? '';
  // `description` and `errorMissingAllDescription` are inline HTML on Sitecore, which the price is appended to on
  // the same line, and one paragraph on Sanity. The error state passes `errorMissingAllDescription` as a string
  // prop, so it is unwrapped there by hand.
  const errorMissingAllDescription = adapter.toRichText(content.errorMissingAllDescription);
  const pricingDataProduct = data?.find(({ productType }) => productType === product);

  let image: JSX.Element | undefined = (
    <Image src={`${getStaticPrefix()}/images/${label}/dynamicpricing/error.svg`} alt="" height={imageHeight} />
  );
  let secondaryDescription = (
    <Box padding={2}>
      <ErrorIcon size="medium" color="feedbackError" />
    </Box>
  );

  const displayError = !data || hasError || !pricingDataProduct;
  const link = linkWithUrl(content.link);

  const typeOfLayout = content.useVerticalLayout ? 'vertical' : 'horizontal';

  if (displayError) {
    return (
      <DynamicPriceCardComponent
        layout={typeOfLayout}
        product={product}
        title={title}
        description={stripOuterParagraph(errorMissingAllDescription) ?? ''}
        secondaryDescription={secondaryDescription}
        footnote={content.errorMissingAllFootnoteText ?? ''}
        image={image}
        cta={link}
      />
    );
  }

  const relevantPricingDataSlices = getRelevantSlices(pricingDataProduct.slices);

  let description = <RichText inline html={errorMissingAllDescription} />;

  const currentPrice = relevantPricingDataSlices.currentSlice?.price.totalVat?.inclVat;

  if (relevantPricingDataSlices.currentSlice && currentPrice) {
    const nextPriceDate = addHours(relevantPricingDataSlices.currentSlice.start, 1);
    const nextPriceTime = formatAmsterdamTime(nextPriceDate);

    description = (
      <>
        <RichText inline html={format(adapter.toRichText(content.description) ?? '', { nextPriceTime })} />

        <Text whiteSpace="nowrap">{`${currency.euroFiveDecimals(currentPrice)} / ${
          pricingDataProduct.denotationType
        }`}</Text>
      </>
    );

    const popoverTopic = content.secondaryDescriptionTitle ?? '';

    secondaryDescription = (
      <Popover
        title={popoverTopic}
        trigger={
          <IconButton label={popoverTopic}>
            <InfoIcon size="medium" />
          </IconButton>
        }>
        <RichText html={adapter.toRichText(content.secondaryDescriptionContent) ?? ''} />
      </Popover>
    );

    image = imagePaths?.[label]?.[product] ? (
      <Image src={`${getStaticPrefix()}/images/${label}/${imagePaths[label][product]}`} alt="" height={imageHeight} />
    ) : undefined;
  }

  let footnote = (
    <RichText
      html={(currentPrice ? content.errorMissingNextPriceFootnoteText : content.errorMissingAllFootnoteText) ?? ''}
    />
  );

  const nextPrice = relevantPricingDataSlices.nextSlice?.price.totalVat?.inclVat;

  if (relevantPricingDataSlices.nextSlice && nextPrice) {
    const dayIndication = isTodayAmsterdam(relevantPricingDataSlices.nextSlice.start)
      ? content.todayText
      : content.tomorrowText;
    const nextPriceTime = formatAmsterdamTime(relevantPricingDataSlices.nextSlice.start);

    footnote = (
      <>
        <RichText html={format(content.footnoteText ?? '', { dayIndication: dayIndication ?? '', nextPriceTime })} />

        <Text whiteSpace="nowrap">{`${currency.euroFiveDecimals(nextPrice)} / ${
          pricingDataProduct.denotationType
        }`}</Text>
      </>
    );
  }

  return (
    <DynamicPriceCardComponent
      layout={typeOfLayout}
      product={product}
      title={title}
      description={description}
      secondaryDescription={secondaryDescription}
      footnote={footnote}
      image={image}
      cta={link}
    />
  );
};

type DynamicPriceCardComponentProps = {
  layout?: 'horizontal' | 'vertical';
  product: Product;
  title: string;
  description: string | ReactElement;
  secondaryDescription: ReactElement;
  footnote: string | ReactElement;
  image?: ReactElement;
  cta?: { text?: string; linkType?: string; href: string };
};

const DynamicPriceCardComponent: FC<DynamicPriceCardComponentProps> = ({
  layout = 'horizontal',
  product,
  title,
  description,
  secondaryDescription,
  footnote,
  image,
  cta,
}) => {
  const mobileLayoutApplied = !useMediaQuery('md');

  const textContent = (
    <Stack.Item grow="false" key="textContent">
      <Grid
        gap={1}
        alignY="center"
        gridTemplateAreas={`
                "icon description"
                "  .   footnote  "
              `}>
        <Grid.Item gridArea="icon">
          {product === 'electricity' ? <ElectricityIcon color="iconElectricity" /> : <GasIcon color="iconGas" />}
        </Grid.Item>
        <Grid.Item gridArea="description">
          <Stack direction="row" alignY="center">
            <Text size="BodyL" weight="bold" as="p">
              {description}
            </Text>
            {secondaryDescription}
          </Stack>
        </Grid.Item>
        <Grid.Item gridArea="footnote">
          <Text size="BodyS" as="p">
            {footnote}
          </Text>
        </Grid.Item>
      </Grid>
    </Stack.Item>
  );

  const imageContent = <Fragment key="imageContent">{!mobileLayoutApplied && image}</Fragment>;
  const allContent = [textContent, imageContent];

  if (layout === 'vertical') {
    allContent.reverse();
  }

  return (
    <StretchBucket title={title} buttonText={cta?.text} buttonLinkType={cta?.linkType} buttonLink={cta?.href}>
      <Stretch height>
        <Stack
          gap={6}
          direction={layout === 'horizontal' ? 'row' : 'column'}
          alignX={layout === 'horizontal' ? 'justify' : 'center'}
          alignY={layout === 'horizontal' ? 'center' : 'start'}>
          {allContent}
        </Stack>
      </Stretch>
    </StretchBucket>
  );
};

const getRelevantSlices = (slices: TransformedSlice[] | undefined) => {
  if (!slices) {
    return {
      currentSlice: undefined,
      nextSlice: undefined,
    };
  }
  const indexCurrentSlice = slices.findIndex(slice => slice.isCurrentHour);
  return {
    currentSlice: slices[indexCurrentSlice],
    nextSlice: indexCurrentSlice === -1 ? undefined : slices[indexCurrentSlice + 1],
  };
};

export const DynamicPriceCardElectricityV2: FC = () => <DynamicPriceCard product="electricity" />;
export const DynamicPriceCardGasV2: FC = () => <DynamicPriceCard product="gas" />;
